import {
  Button,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import React, { ReactNode, useEffect, useState } from "react";
import Resizer from "react-image-file-resizer";
import { PageContainer } from "../../components/PageContainer";
import { useMutation, useQuery } from "@apollo/client";
import { GET_CONFIG, UPDATE_CONFIG } from "../config/graphql";
import { Config } from "../../__generated__/graphql";
import { toast } from "react-toastify";
import { UpdateMiscConfigButton } from "../links/updateMiscConfig";
import { getImageUrlFromResult, getUpdatedImageUrl } from "../post/edit/helpers";
import { useFieldArray, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { ErrorRenderer } from "../../components/ErrorRenderer";
import { ADD_IMAGE } from "../band/graphql";
import { s3Put } from "../band/s3";

const schema = yup
  .object({
    config: yup.array().of(
      yup
        .object({
          keyId: yup.string(),
          value: yup.string(),
        })
        .test("unique", "keyId must be unique", function (value) {
          const propertyName = "keyId";
          if (!value || !value[propertyName]) {
            return true;
          }

          const { path } = this;
          const options = [...this.parent];
          const currentIndex = options.indexOf(value);

          const subOptions = options.slice(0, currentIndex);

          if (
            subOptions.some(
              (option) => option[propertyName] === value[propertyName]
            )
          ) {
            throw this.createError({
              path: `${path}.${propertyName}`,
              message: "keyId must be unique",
            });
          }

          return true;
        })
    ),
  })
  .required();

export const Map = () => {
  const { data, refetch } = useQuery(GET_CONFIG);

  const configs = (data?.configs as Config[]) || undefined;

  useEffect(() => {
    const handleFocus = () => {
      refetch();
    };

    window.addEventListener("focus", handleFocus);

    return () => {
      window.removeEventListener("focus", handleFocus);
    };
  }, [refetch]);
  
  const maps =
    JSON.parse(configs?.find((item) => item.key === "MAPS")?.value ?? "{}") ??
    {};

  const mapValues: { keyId: string; value: string }[] = Object.keys(maps).map(
    (key) => {
      const data = maps[key];
      return {
        keyId: key,
        value: data,
      };
    }
  );

  // if (!mapValues || mapValues.length === 0) return null;

  return <MapForm config={mapValues} />;
};

interface IMap {
  config: { keyId: string; value: string }[];
}
export const MapForm = ({ config }: IMap) => {
  const {
    register,
    handleSubmit,
    watch,
    reset,
    control,
    formState: { errors },
    setValue,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      config: config ?? ([] as { keyId: string; value: string }[]),
    },
  });

  // console.log("config", config)

  const { fields, replace, append, remove, swap, move, insert, update } =
    useFieldArray({
      control, // control props comes from useForm (optional: if you are using FormContext)
      name: "config", // unique name for your Field Array
    });

  const [image, setImage] = useState<string>();
  const [file, setFile] = useState<string>();
  const [forDeletion, updateForDeletion] = useState<string[]>([]);
  const [uploadReady, setUploadReady] = useState(false);
  const [fileName, setFileName] = useState<string>();
  const [updateConfig] = useMutation(UPDATE_CONFIG, {
    awaitRefetchQueries: true,
    refetchQueries: ["config"],
    onCompleted: () => {
      toast("Map Url Updated");
    },
  });
  const [getImageUrl, { data, error }] = useMutation(ADD_IMAGE, {});

  const onFileChange = async (event: any, callback: (url: string) => void) => {
    try {
      const file = event.target.files[0];
      const date = new Date().getTime();

      const split = file.name.split(".");
      // const ext = split[split.length - 1];
      // setFileName(`map/${split[0]}-${date}.webp`);
      const fileName = `map/${split[0]}-${date}.webp`;

      // RESIZER
      const resizeFile = (file: any) =>
        new Promise((resolve) => {
          Resizer.imageFileResizer(
            file,
            2000, // maxWidth
            2000, // maxHeight
            "webp",
            100,
            0,
            (uri) => {
              resolve(uri);
            },
            "base64"
            // 1500, // minWidth?
            // 1500 // minHeight
          );
        });

        // gets the file as a URL
      let obj = URL.createObjectURL(event.target.files[0]);
      // creates a blob
      const temp = await fetch(obj).then((r) => r.blob());
      // resizes it.
      const image = (await resizeFile(temp)) as string;
      // setImage(obj);
      // setFile(file);

      
      // gets a url again
      // obj = URL.createObjectURL(image);
      
      const fileBlob = await fetch(image).then((r) => r.blob());
      const res = await getImageUrl({ variables: { image: fileName } });
      const url = getImageUrlFromResult(res?.data?.addImage)

      if (!res.data?.addImage || !fileBlob) return;

      // const fileBlob = await fetch(resizedFile).then((r) => r.blob());
      // const image = URL.createObjectURL(fileBlob)
      await s3Put(res.data?.addImage, fileBlob);

      callback(url);
    } catch (err) {
      console.log(err);
    }
  };

  const onSubmit = (form: { config: { keyId: string; value: string }[] }) => {
    const withoutDeleted = form.config.filter(
      (item) => !forDeletion.includes(item.keyId)
    );

    const ready = withoutDeleted.reduce((acc, next) => {
      // @ts-ignore
      acc[next.keyId] = next.value;

      return acc;
    }, {});

    console.log("ready", JSON.stringify(ready));

    const settings = withoutDeleted.map((item) => {
      return { key: item.keyId, value: item.value };
    });

    updateConfig({
      variables: {
        settings: {
          key: "MAPS",
          value: JSON.stringify(ready),
        },
      },
    });
  };

  return (
    <PageContainer title="Select an image">
      <div
        style={{
          display: "flex",
          flex: 1,
          flexDirection: "row",
          minHeight: "100%",
          alignItems: "center",
          minWidth: 800,
          gap: 40,
          marginBottom: 40,
        }}
      >
        <h3>
          Changes to the map will not be visible until the config is published.
        </h3>
        <UpdateMiscConfigButton />
      </div>
      {/* {mapUrl && <img src={getUpdatedImageUrl(mapUrl)} height={500} />} */}
      <Button
        onClick={() =>
          append({
            keyId: "",
            value: "",
          })
        }
        color="success"
        variant="contained"
        style={{ height: "100%" }}
      >
        Add New
      </Button>
      {/* @ts-ignore */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Url</TableCell>
              <TableCell>actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {fields.map((item, index) => {
              return (
                <TableRow key={item.id} id={item.id}>
                  {/* <input
                    type="hidden"
                    defaultValue={item.keyId ?? ""}
                    {...register(`config.${index}.keyId`)}
                  /> */}
                  {/* @ts-ignore */}
                  <TableCell>
                    {/* <TextField
                    fullWidth
                    id="outlined-basic"
                    label="value"
                    variant="outlined"
                    {...register(`config.${index}.keyId`)}
                  /> */}
                    <TextField
                      // fullWidth
                      id="outlined-basic"
                      key={`value-keyId-${item.id}`}
                      label="Name"
                      variant="outlined"
                      // prevent label getting in the way
                      InputLabelProps={{
                        shrink: true,
                      }}
                      defaultValue={item.keyId}
                      error={!!errors.config?.[index]?.keyId?.message}
                      {...register(`config.${index}.keyId`)}
                    />
                  </TableCell>
                  {/* @ts-ignore */}
                  <TableCell
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      maxWidth: "50%",
                    }}
                  >
                    {/* <TextField
                    fullWidth
                    id="outlined-basic"
                    label="value"
                    variant="outlined"
                    {...register(`config.${index}.value`)}
                  /> */}
                    <TextField
                      // fullWidth
                      id="outlined-basic"
                      key={`value-field-${item.id}`}
                      label="Url"
                      variant="outlined"
                      // prevent label getting in the way
                      InputLabelProps={{
                        shrink: true,
                      }}
                      style={{ width: 500 }}
                      defaultValue={item.value}
                      error={!!errors.config?.[index]?.value?.message}
                      {...register(`config.${index}.value`)}
                    />
                    <img src={item.value} alt="" width={280} />
                  </TableCell>
                  <TableCell align="left">
                    <Input
                      id={`file-input-${item.id}`}
                      key={`file-input-${item.id}`}
                      type="file"
                      onChange={(event) =>
                        onFileChange(event, (url: string) => {
                          console.log("UPDATING RECORD", url);
                          // update record.
                          update(index, { ...item, value: url });
                          // setValue(`config.${index}.value`, url);
                        })
                      }
                    />
                    <Button
                      disabled={forDeletion.includes(item?.keyId ?? "")}
                      onClick={() => {
                        // @ts-ignore
                        if (item.keyId === "") {
                          remove(index);
                        } else {
                          // deleteConfig({ variables: { key: item.keyId } });
                          updateForDeletion([...forDeletion, item.keyId ?? ""]);
                        }
                      }}
                      color="error"
                      variant="contained"
                      style={{
                        // marginTop: 18,
                        marginBottom: 20,
                        // marginRight: 8,
                      }}
                    >
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <Button
          type="submit"
          variant="contained"
          style={{ marginTop: 18, marginBottom: 18 }}
        >
          Save
        </Button>
      </form>

      <ErrorRenderer errors={errors} />
      {/* <EditImageContainer>
        <Input type="file" onChange={onFileChange} />
        <Button
          disabled={!file}
          onClick={handleClick}
          variant="contained"
          style={{ alignSelf: "flex-end" }}
        >
          Proceed
        </Button>
      </EditImageContainer> */}
      {/* <img src={getUpdatedImageUrl(image ?? "")} alt="" width={"75%"} /> */}
    </PageContainer>
  );
};

type PropsWithChildren<P> = P & { children?: ReactNode };

export const EditImageContainer: React.FunctionComponent<
  PropsWithChildren<{}>
> = ({ children }) => {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        paddingBottom: 46,
      }}
    >
      {children}
    </div>
  );
};
